import React, { useContext, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { useTheme } from 'emotion-theming'
import { string } from 'prop-types'

import { Pagination, PulseBarPreloader } from '@jelly/components'
import { SectionContainer, SectionTitle, Link } from '@jelly/ui'
import { smoothScrollIntoView } from '@jelly/utils'

import { UserActivityContext } from './UserActivityProvider'
import { ActivityFeedResults } from './ActivityFeedResults'

const FeedWrapper = styled.div`
	height: 100%;
	position: relative;
	width: 100%;
`
const LoadingContainer = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	height: 100%;
	left: 0;
	position: absolute;
	right: 0;
	top: 5rem;
	width: 100%;
	z-index: 1;
`
const NoActivity = styled.div`
	color: ${({ theme }) => theme.colors.gray[400]};
	font-size: 1.3rem;
	margin: 2rem 0 1rem 0;
	text-align: center;
`

UserActivityFeed.propTypes = {
	firstName: string,
	profilePath: string.isRequired,
}

/**
 * Results Container displaying Activity based on User
 *
 * @param {String} props.firstName :: first name of the member
 * @param {String} props.profilePath :: path of the current page
 */
export function UserActivityFeed({ firstName, profilePath }) {
	const {
		colors: { primary },
	} = useTheme()
	const { isLoading, activityResults, activitySearch } = useContext(UserActivityContext)
	const activityFeedRef = useRef(null)

	useEffect(() => {
		if (isLoading) {
			smoothScrollIntoView(activityFeedRef.current)
		}
	}, [isLoading])

	return (
		<FeedWrapper ref={activityFeedRef}>
			<SectionContainer>
				<SectionTitle>{firstName}'s Activity</SectionTitle>
				{activityResults && activityResults.length !== 0 ? (
					<>
						{isLoading && (
							<LoadingContainer>
								<PulseBarPreloader width="30%" height="10%" fill={primary} />
							</LoadingContainer>
						)}
						<ActivityFeedResults
							firstName={firstName}
							isLoading={isLoading}
							activityResults={activityResults}
						/>
						{activitySearch.num_pages > 1 && (
							<Pagination path={profilePath} lastPage={activitySearch.num_pages} />
						)}
					</>
				) : (
					<NoActivity>
						{firstName} doesn't have any activity yet.
						<br />
						Browse our latest advice{' '}
						<Link href="/advice/answers" variant="secondary">
							here
						</Link>
						.
					</NoActivity>
				)}
			</SectionContainer>
		</FeedWrapper>
	)
}
